import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  ArrowLeft, Users, Clock, MapPin, Banknote, CheckCircle, 
  XCircle, AlertCircle, Search, Filter, RefreshCw, UserX,
  Phone, Ticket
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusOperatorBookings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [activeTab, setActiveTab] = useState('pending'); // pending, all
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      navigate('/bus/operator/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([
        api.get('/bus/operators/bookings/pending-payments'),
        api.get('/bus/operators/bookings?limit=50')
      ]);
      
      setPendingPayments(pendingRes.data.bookings || []);
      setBookings(allRes.data.bookings || []);
    } catch (error) {
      if (error.response?.status === 404) {
        navigate('/bus/operator/apply');
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load bookings',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async (bookingId) => {
    setConfirmingId(bookingId);
    try {
      const response = await api.put(`/bus/operators/bookings/${bookingId}/confirm-payment`);
      toast({
        title: 'Payment Confirmed',
        description: response.data.message,
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to confirm payment',
        variant: 'destructive'
      });
    } finally {
      setConfirmingId(null);
    }
  };

  const handleMarkNoShow = async (bookingId) => {
    if (!confirm('Mark this passenger as no-show? This will release their seat(s).')) {
      return;
    }
    
    try {
      await api.put(`/bus/operators/bookings/${bookingId}/mark-no-show`);
      toast({
        title: 'Marked as No-Show',
        description: 'Seat(s) have been released',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update booking',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (paymentStatus === 'pending') {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          Payment Pending
        </span>
      );
    }
    
    switch (status) {
      case 'confirmed':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            Confirmed
          </span>
        );
      case 'completed':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            Cancelled
          </span>
        );
      case 'no_show':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            No Show
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {status}
          </span>
        );
    }
  };

  const filteredBookings = activeTab === 'pending' 
    ? pendingPayments 
    : bookings.filter(b => 
        !searchQuery || 
        b.ticketCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.passengers?.some(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()))
      );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/bus/operator">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Manage Bookings</h1>
              <p className="text-indigo-100">View and confirm passenger payments</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 -mt-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="p-4 bg-yellow-50 border-yellow-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-yellow-700">Pending Payments</p>
                <p className="text-2xl font-bold text-yellow-800">{pendingPayments.length}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-green-700">Total Bookings</p>
                <p className="text-2xl font-bold text-green-800">{bookings.length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs & Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex gap-2">
            <Button
              variant={activeTab === 'pending' ? 'default' : 'outline'}
              onClick={() => setActiveTab('pending')}
              className={activeTab === 'pending' ? 'bg-indigo-600' : ''}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Pending ({pendingPayments.length})
            </Button>
            <Button
              variant={activeTab === 'all' ? 'default' : 'outline'}
              onClick={() => setActiveTab('all')}
              className={activeTab === 'all' ? 'bg-indigo-600' : ''}
            >
              <Ticket className="h-4 w-4 mr-2" />
              All Bookings
            </Button>
          </div>
          
          {activeTab === 'all' && (
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by ticket code or passenger name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          )}
          
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Bookings List */}
        {filteredBookings.length === 0 ? (
          <Card className="p-8 text-center">
            {activeTab === 'pending' ? (
              <>
                <CheckCircle className="h-16 w-16 text-green-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Pending Payments</h3>
                <p className="text-gray-500">All cash payments have been confirmed</p>
              </>
            ) : (
              <>
                <Ticket className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Bookings Found</h3>
                <p className="text-gray-500">No bookings match your search</p>
              </>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <Card key={booking.id} className="p-4" data-testid={`booking-${booking.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-mono font-bold text-indigo-600">
                        {booking.ticketCode}
                      </span>
                      {getStatusBadge(booking.status, booking.paymentStatus)}
                      <span className="text-xs text-gray-500">
                        {booking.paymentMethod === 'cash' ? '💵 Cash' : '📱 Mobile'}
                      </span>
                    </div>
                    
                    {/* Route & Time */}
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {booking.route?.origin} → {booking.alighting?.city || booking.route?.destination}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {booking.trip?.date} {booking.trip?.departureTime}
                      </span>
                    </div>
                    
                    {/* Passengers */}
                    <div className="bg-gray-50 rounded-lg p-3 mt-2">
                      <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {booking.passengers?.length || 1} Passenger(s)
                      </p>
                      {booking.passengers?.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div>
                            <span className="font-medium">{p.name}</span>
                            <span className="text-gray-500 ml-2">Seat {p.seat}</span>
                          </div>
                          <span className="flex items-center gap-1 text-gray-500">
                            <Phone className="h-3 w-3" />
                            {p.phone}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="text-right sm:min-w-[140px]">
                    <p className="text-2xl font-bold text-gray-800 mb-2">
                      K{booking.totalFare?.toLocaleString()}
                    </p>
                    
                    {booking.paymentStatus === 'pending' && booking.paymentMethod === 'cash' && (
                      <div className="space-y-2">
                        <Button
                          onClick={() => handleConfirmPayment(booking.id)}
                          disabled={confirmingId === booking.id}
                          className="w-full bg-green-600 hover:bg-green-700"
                          data-testid={`confirm-payment-${booking.id}`}
                        >
                          {confirmingId === booking.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Confirm Paid
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleMarkNoShow(booking.id)}
                          className="w-full text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`no-show-${booking.id}`}
                        >
                          <UserX className="h-4 w-4 mr-1" />
                          No Show
                        </Button>
                      </div>
                    )}
                    
                    {booking.paymentStatus === 'paid' && (
                      <div className="flex items-center justify-end gap-1 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-sm">Paid</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusOperatorBookings;
